import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, AlertTriangle, Globe, Server, Monitor,
  Clock, Timer, Package, Activity, Wifi, Zap,
  RefreshCw, DownloadCloud, Upload, Shield, Cloud , Router,
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell
} from 'recharts';

const Ping = ({ results }) => {
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [packetLossRate, setPacketLossRate] = useState(0);

  const [animate, setAnimate] = useState(true);
  
  
  // Determine connection quality
  const getConnectionQuality = () => {
    if (results.status !== 'Success') return 'poor';
    if (packetLossRate > 20) return 'poor';
    if (packetLossRate > 5 || results.latency.average > 150) return 'moderate';
    return 'good';
  };
  
  const quality = getConnectionQuality();
  
  useEffect(() => {
    if (results && results.type === 'ping') {
      // Calculate packet loss rate
      const lossRate = results.packets ? 
        Math.round(((results.packets.sent - results.packets.received) / results.packets.sent) * 100) : 0;
      setPacketLossRate(lossRate);
      
      // Generate sample time series data based on provided latency values
      if (results.latency && results.timestamps) {
        const seriesData = results.timestamps.map((time, index) => ({
          time: time,
          responseTime: results.response_times ? results.response_times[index] : results.latency.average
        }));
        setTimeSeriesData(seriesData);
      } else {
        // Generate mock data if timestamps aren't available
        const mockData = Array(10).fill().map((_, i) => {
          const base = results.latency.average || 50;
          const variance = results.latency.max - results.latency.min || 10;
          return {
            time: `T${i+1}`,
            responseTime: Math.max(1, base + (Math.random() * variance * 0.8) - (variance * 0.4))
          };
        });
        setTimeSeriesData(mockData);
      }
    }
  }, [results]);
  
  // Get network health status
  const getNetworkHealth = () => {
    if (!results || results.type !== 'ping') return { status: 'Unknown', color: 'gray' };
    
    if (results.status !== 'Success') return { status: 'Down', color: 'red' };
    
    if (packetLossRate > 20) return { status: 'Poor', color: 'red' };
    if (packetLossRate > 5) return { status: 'Fair', color: 'yellow' };
    
    const avgLatency = results.latency ? results.latency.average : 0;
    if (avgLatency > 200) return { status: 'Degraded', color: 'yellow' };
    if (avgLatency > 100) return { status: 'Good', color: 'blue' };
    return { status: 'Excellent', color: 'green' };
  };
  
  // Generate network analysis text
  const getNetworkAnalysis = () => {
    if (!results || results.type !== 'ping') return 'No data available for analysis.';
    
    const health = getNetworkHealth();
    let analysis = `Connection to ${results.target} is ${health.status.toLowerCase()}.`;
    
    if (results.status !== 'Success') {
      return `${analysis} Unable to establish connection. The target host may be down, blocked by a firewall, or network connectivity issues are present.`;
    }
    
    if (packetLossRate > 0) {
      analysis += ` Packet loss of ${packetLossRate}% detected, which `;
      if (packetLossRate > 20) analysis += 'indicates significant network congestion or reliability issues.';
      else if (packetLossRate > 5) analysis += 'suggests some network instability.';
      else analysis += 'is within acceptable range but worth monitoring.';
    } else {
      analysis += ' No packet loss detected.';
    }
    
    if (results.latency) {
      if (results.latency.average > 200) {
        analysis += ' High latency may impact real-time applications.';
      } else if (results.latency.average < 50) {
        analysis += ' Low latency indicates excellent connectivity.';
      }
      
      const jitter = results.latency.max - results.latency.min;
      if (jitter > 50) {
        analysis += ' High variance in response times suggests network instability.';
      }
    }
    
    return analysis;
  };
  
  // Colors for charts
  const colors = {
    primary: '#0ea5e9',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#8b5cf6',
    background: 'rgba(30, 41, 59, 0.8)',
    grid: 'rgba(148, 163, 184, 0.2)',
    text: '#e2e8f0'
  };
  
  const pieColors = [colors.success, colors.danger];
  
  // Packet data for pie chart
  const packetData = [
    { name: 'Received', value: results?.packets?.received || 0 },
    { name: 'Lost', value: (results?.packets?.sent || 0) - (results?.packets?.received || 0) }
  ];
  
  // Rating calculation for connection quality
  const getQualityRating = () => {
    const health = getNetworkHealth();
    switch(health.status) {
      case 'Excellent': return 5;
      case 'Good': return 4;
      case 'Fair': return 3;
      case 'Degraded': return 2;
      default: return 1;
    }
  };
  
  const qualityRating = getQualityRating();
  const qualityData = [
    { name: 'Rating', value: qualityRating },
    { name: 'Gap', value: 5 - qualityRating }
  ];
  
  if (!results || results.type !== 'ping') {
    return <div className="text-center p-8 text-gray-400">No ping results available to display</div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Status Header with Network Quality Rating */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between bg-gray-800 bg-opacity-80 p-6 rounded-xl shadow-lg border border-gray-700/50 backdrop-blur-sm">
        <div className="flex items-center space-x-4 mb-4 md:mb-0">
          {results.status === 'Success' ? (
            <CheckCircle className="text-emerald-400" size={32} />
          ) : (
            <AlertTriangle className="text-red-400" size={32} />
          )}
          <div>
            <h1 className="text-2xl font-bold text-white">Network Diagnostic</h1>
            <p className="text-gray-400">Real-time Connection Status</p>
          </div>
        </div>
        
        <div className="flex flex-col items-end">
          <div className={`px-4 py-2 rounded-full ${
            results.status === 'Success' ? 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/30' : 'bg-red-400/10 text-red-400 border border-red-400/30'
          }`}>
            {results.status === 'Success' ? 'Connection Active' : 'Connection Failed'}
          </div>
          
          <div className="mt-2 flex items-center">
            <span className="text-xs text-gray-400 mr-2">Quality:</span>
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Zap 
                  key={star}
                  size={16}
                  className={star <= qualityRating ? 'text-yellow-400' : 'text-gray-600'}
                  fill={star <= qualityRating ? 'currentColor' : 'none'}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Visual Network Diagram */}
      <div className="bg-gray-800 bg-opacity-60 p-6 rounded-xl border border-gray-700">
        <div className="flex items-center space-x-3 mb-4">
          <Wifi className="text-indigo-400" size={24} />
          <h2 className="text-lg font-semibold text-white">Network Visualization</h2>
        </div>
        
        <div className="h-64 relative flex items-center justify-center">
          {/* Source (User) */}
          <div className="absolute left-10 flex flex-col items-center">
            <Monitor className="text-blue-400 mb-2" size={36} />
            <div className="text-center">
              <p className="text-sm font-medium text-white">Your Device</p>
            </div>
          </div>
          
          {/* Router/Gateway */}
          <div className="absolute left-1/4 flex flex-col items-center">
            <Router className="text-cyan-400 mb-2" size={36} />
            <div className="text-center">
              <p className="text-sm font-medium text-white">Router</p>
            </div>
          </div>
          
          {/* Internet Cloud */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center">
            <Cloud className="text-purple-400 mb-2" size={42} />
            <div className="text-center">
              <p className="text-sm font-medium text-white">Internet</p>
            </div>
          </div>
          
          {/* Target Server */}
          <div className="absolute right-10 flex flex-col items-center">
            <Server className="text-emerald-400 mb-2" size={36} />
            <div className="text-center">
              <p className="text-sm font-medium text-white">{results.target}</p>
              <p className="text-xs text-gray-400">{results.resolved_ip}</p>
            </div>
          </div>
          
          {/* Connection Lines */}
          <svg className="absolute inset-0 w-full h-full" style={{ zIndex: -1 }}>
            {/* Line: Device to Router */}
            <line 
              x1="90" y1="128" 
              x2="25%" y2="128" 
              stroke="#4B5563" 
              strokeWidth="3"
            />
            
            {/* Line: Router to Internet */}
            <line 
              x1="25%" y1="128" 
              x2="50%" y2="128" 
              stroke="#4B5563" 
              strokeWidth="3"
            />
            
            {/* Line: Internet to Server */}
            <line 
              x1="50%" y1="128" 
              x2="calc(100% - 90px)" y2="128" 
              stroke={results.status === 'Success' ? "#4B5563" : "#EF4444"} 
              strokeWidth="3"
              strokeDasharray={results.status !== 'Success' ? "10,10" : ""}
            />
            
            {/* Animated Data Packets */}
            {animate && results.status === 'Success' && (
              <>
                {/* Request Packet */}
                <circle 
                  className="animate-ping" 
                  cx="90" cy="128" r="5" 
                  fill="#3B82F6" 
                  style={{ 
                    animationDuration: "3s",
                    transformOrigin: "center",
                    transform: `translateX(${quality === 'good' ? 'calc(100% - 180px)' : quality === 'moderate' ? '50%' : '30%'})`,
                    opacity: quality === 'poor' ? 0 : 1
                  }} 
                />
                
                {/* Response Packet */}
                <circle 
                  className="animate-ping" 
                  cx="calc(100% - 90px)" cy="128" r="5" 
                  fill="#10B981" 
                  style={{ 
                    animationDuration: "3s",
                    animationDelay: "1.5s",
                    transformOrigin: "center",
                    transform: "translateX(-100%)",
                    opacity: quality === 'poor' ? 0.5 : 1
                  }} 
                />
              </>
            )}
          </svg>
        </div>
        
        {/* Connection Quality Indicator */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-white">Connection Quality</p>
            <p className="text-sm font-medium text-white">
              {quality === 'good' ? 'Good' : quality === 'moderate' ? 'Moderate' : 'Poor'}
            </p>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full ${
                quality === 'good' ? 'bg-emerald-500' : 
                quality === 'moderate' ? 'bg-yellow-500' : 
                'bg-red-500'
              }`}
              style={{ width: quality === 'good' ? '90%' : quality === 'moderate' ? '50%' : '20%' }}
            ></div>
          </div>
        </div>
      </div>
      
      {/* Target Information with Network Visualization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          {/* Target Domain */}
          <div className="bg-gray-800 bg-opacity-70 p-6 rounded-xl border border-gray-700/50">
            <div className="flex items-center space-x-3 mb-4">
              <Globe className="text-cyan-400" size={24} />
              <h2 className="text-lg font-semibold text-white">Target Domain</h2>
            </div>
            <p className="text-2xl font-bold text-white break-all">{results.target}</p>
          </div>
          
          {/* Server IP */}
          <div className="bg-gray-800 bg-opacity-70 p-6 rounded-xl border border-gray-700/50">
            <div className="flex items-center space-x-3 mb-4">
              <Server className="text-purple-400" size={24} />
              <h2 className="text-lg font-semibold text-white">Server IP</h2>
            </div>
            <p className="text-2xl font-bold text-white">{results.resolved_ip}</p>
            <div className="mt-2 flex items-center text-sm text-gray-400">
              <Shield size={16} className="mr-2" />
              <span>Protocol: {results.protocol || 'ICMPv4'}</span>
            </div>
          </div>
        </div>
        
        {/* Network Visualization - Middle Column */}
        <div className="lg:col-span-2 bg-gray-800 bg-opacity-70 p-6 rounded-xl border border-gray-700/50">
          <div className="flex items-center space-x-3 mb-4">
            <Activity className="text-blue-400" size={24} />
            <h2 className="text-lg font-semibold text-white">Response Time Trends</h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeSeriesData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
                <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time" 
                  stroke={colors.text} 
                  tick={{ fill: colors.text, fontSize: 12 }}
                />
                <YAxis 
                  stroke={colors.text} 
                  tick={{ fill: colors.text, fontSize: 12 }}
                  label={{ 
                    value: 'ms', 
                    angle: -90, 
                    position: 'insideLeft', 
                    fill: colors.text,
                    fontSize: 12
                  }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: colors.background, 
                    borderColor: colors.grid,
                    color: colors.text
                  }}
                  labelStyle={{ color: colors.text }}
                />
                <Line 
                  type="monotone" 
                  dataKey="responseTime" 
                  stroke={colors.primary} 
                  strokeWidth={2} 
                  activeDot={{ r: 6 }} 
                  dot={{ stroke: colors.primary, strokeWidth: 2, r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
     {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Response Times */}
        <div className="bg-gray-800 bg-opacity-70 p-6 rounded-xl border border-gray-700/50">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="text-blue-400" size={24} />
            <div>
              <h2 className="text-lg font-semibold text-white">Response Times</h2>
              <p className="text-xs text-gray-400">(milliseconds)</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-400">Min</p>
              <p className="text-2xl font-bold text-white">{results.latency.min}<span className="text-xs text-gray-500">ms</span></p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Avg</p>
              <p className="text-2xl font-bold text-white">{results.latency.average}<span className="text-xs text-gray-500">ms</span></p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Max</p>
              <p className="text-2xl font-bold text-white">{results.latency.max}<span className="text-xs text-gray-500">ms</span></p>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-sm text-gray-400 mb-2">Jitter: {results.latency.max - results.latency.min}ms</p>
            <div className="h-12">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Min', value: results.latency.min },
                  { name: 'Avg', value: results.latency.average },
                  { name: 'Max', value: results.latency.max }
                ]}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: colors.text }} />
                  <Tooltip contentStyle={{ backgroundColor: colors.background, borderColor: colors.grid, color: colors.text }} />
                  <Bar dataKey="value" fill={colors.primary} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Packet Statistics */}
        <div className="bg-gray-800 bg-opacity-70 p-6 rounded-xl border border-gray-700/50">
          <div className="flex items-center space-x-3 mb-4">
            <Package className="text-emerald-400" size={24} />
            <h2 className="text-lg font-semibold text-white">Packet Stats</h2>
          </div>

          <div className="flex flex-col justify-center items-center mb-2">
            <div className="h-32 w-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={packetData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {packetData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: colors.background, 
                      borderColor: colors.grid,
                      color: colors.text
                    }}
                    formatter={(value, name) => [`${value} packets`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-8 mt-2">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-emerald-400 rounded-full mr-2"></div>
                <span className="text-xs text-gray-400">Received</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-400 rounded-full mr-2"></div>
                <span className="text-xs text-gray-400">Lost</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-gray-400">Sent</p>
              <p className="text-2xl font-bold text-white">{results.packets.sent}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Received</p>
              <p className="text-2xl font-bold text-white">{results.packets.received}</p>
            </div>
            {packetLossRate > 0 && (
              <div className="col-span-2">
                <p className="text-sm text-gray-400">Packet Loss</p>
                <div className="flex items-center">
                  <p className="text-2xl font-bold text-red-400 mr-2">{packetLossRate}%</p>
                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full" 
                      style={{ width: `${packetLossRate}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Network Analysis and Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Network Analysis */}
        <div className="bg-gray-800 bg-opacity-70 p-6 rounded-xl border border-gray-700/50">
          <div className="flex items-center space-x-3 mb-4">
            <Activity className="text-sky-400" size={24} />
            <h2 className="text-lg font-semibold text-white">Analysis</h2>
          </div>
          <p className="text-gray-300 leading-relaxed">{getNetworkAnalysis()}</p>
          
          {/* Network Health Indicators */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <RefreshCw size={16} className="text-cyan-400" />
              <span className="text-sm text-gray-400">
                Stability: {packetLossRate > 10 ? 'Poor' : packetLossRate > 2 ? 'Fair' : 'Good'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Zap size={16} className="text-yellow-400" />
              <span className="text-sm text-gray-400">
                Speed: {results.latency.average > 150 ? 'Slow' : results.latency.average > 50 ? 'Average' : 'Fast'}
              </span>
            </div>
          </div>
        </div>
        
        {/* Recommendations */}
        <div className="bg-gray-800 bg-opacity-70 p-6 rounded-xl border border-gray-700/50">
          <div className="flex items-center space-x-3 mb-4">
            <DownloadCloud className="text-indigo-400" size={24} />
            <h2 className="text-lg font-semibold text-white">Recommendations</h2>
          </div>
          <ul className="space-y-2 text-gray-300">
            {packetLossRate > 10 && (
              <li className="flex items-start">
                <AlertTriangle size={16} className="text-red-400 mt-1 mr-2" />
                <span>Check for network congestion or interference.</span>
              </li>
            )}
            {results.latency.average > 150 && (
              <li className="flex items-start">
                <Clock size={16} className="text-yellow-400 mt-1 mr-2" />
                <span>High latency detected. Consider optimizing connection or changing servers.</span>
              </li>
            )}
            {(results.latency.max - results.latency.min) > 50 && (
              <li className="flex items-start">
                <Activity size={16} className="text-orange-400 mt-1 mr-2" />
                <span>Significant jitter detected. Network stability issues may be present.</span>
              </li>
            )}
            {results.status !== 'Success' && (
              <li className="flex items-start">
                <AlertTriangle size={16} className="text-red-400 mt-1 mr-2" />
                <span>Connection failed. Check firewall settings or verify the target is online.</span>
              </li>
            )}
            {(results.status === 'Success' && packetLossRate < 5 && results.latency.average < 100) && (
              <li className="flex items-start">
                <CheckCircle size={16} className="text-green-400 mt-1 mr-2" />
                <span>Network performance is optimal. No action required.</span>
              </li>
            )}
          </ul>
        </div>
        {/* Full analysis report */}
      <div className="mt-6 bg-black/30 p-4 rounded-lg border border-gray-800">
        <h3 className="text-sm font-medium text-white mb-2">Diagnostic Summary</h3>
        <p className="text-xs text-gray-300 leading-relaxed">
          {getNetworkAnalysis()} The connection to {results.target} ({results.resolved_ip}) shows 
          an average latency of {results.latency.average}ms with {packetLossRate > 0 ? 
          `${packetLossRate}% packet loss` : 'no packet loss'}. The time-to-live (TTL) values 
          indicate approximately network hops remaining.
        </p>
      </div>

      </div>
    </div>
  );
};

export default Ping;
